import { Comment } from "@prisma/client";
import { HeadingLevel, Paragraph, TextRun } from "docx";
import keyBy from "lodash/keyBy";
import { TFunction } from "next-i18next";

import { getModifiedConfigParamValue } from "@/components/ImmigramForm/elements/common";
import { ImmigramFormElementType } from "@/components/ImmigramForm/elements/common";

import {
  renderProseMirrorSchemaToGoogleDoc,
  renderProseMirrorSchemaToGoogleDocWithHighlights,
} from "./proseMirror";
import { FormWithDataAndConfig } from "./useGenerateDocFromConfigs";

function formatCommentDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

// Map comment color tokens to hex colors for docx highlighting
const COMMENT_COLOR_MAP: Record<string, string> = {
  yellow: "FFD54F",
  purple: "9C27B0",
  sea: "26C6DA",
  bruise: "5C6BC0",
  morning: "FFA726",
  paw: "8D6E63",
  pods: "26A69A",
  green: "66BB6A",
};

function getCommentColorHex(color?: string | null): string | undefined {
  if (!color) return undefined;

  const normalized = color.trim().toLowerCase();

  // 1) Сначала пробуем известные токены (yellow, purple, и т.д.)
  if (COMMENT_COLOR_MAP[normalized]) {
    return COMMENT_COLOR_MAP[normalized];
  }

  // 2) Если в базе уже лежит hex, используем его напрямую
  // Поддерживаем варианты "#ffaabb" и "ffaabb"
  const hexMatch = normalized.match(/^#?([0-9a-f]{6})$/i);
  if (hexMatch) {
    return hexMatch[1].toUpperCase();
  }

  // 3) Иначе цвета нет / формат неизвестен — не красим
  return undefined;
}

/**
 * Renders a single comment with its replies
 */
function renderCommentWithReplies(
  comment: Comment,
  allComments: Comment[]
): Paragraph[] {
  const result: Paragraph[] = [];

  // Render the comment itself
  const author = comment.from === "manager" ? "Manager" : "Client";
  const commentDate =
    comment.created instanceof Date
      ? comment.created
      : new Date(comment.created);
  const dateStr = formatCommentDate(commentDate);

  // Автор и дата на одной строке: "Автор Дата и время:"
  result.push(
    new Paragraph({
      children: [
        new TextRun({
          text: author,
          bold: true,
        }),
        new TextRun({
          text: ` ${dateStr}:`,
        }),
      ],
    })
  );

  // Текст комментария на следующей строке
  const commentParagraphs = renderProseMirrorSchemaToGoogleDoc(
    comment.text,
    ""
  );
  if (commentParagraphs) {
    result.push(...commentParagraphs);
  }

  // Find and render replies to this comment
  // Показываем все ответы, включая черновики (isDraft)
  const replies = allComments
    .filter((c) => c.replyId === comment.id && !c.deleted)
    .sort((a, b) => {
      const dateA = a.created instanceof Date ? a.created : new Date(a.created);
      const dateB = b.created instanceof Date ? b.created : new Date(b.created);
      return dateA.getTime() - dateB.getTime();
    });

  for (const reply of replies) {
    result.push(...renderCommentWithReplies(reply, allComments));
  }

  return result;
}

/**
 * Renders only general (non-fragment) comments for a field
 * (comments without sectionFrom/sectionTo). These идут в конец поля.
 */
function renderGeneralCommentsForField(
  fieldId: string | undefined,
  fieldName: string,
  comments: Comment[]
): Paragraph[] {
  if (!fieldId) {
    return [];
  }

  const threadName = `${fieldId}_${fieldName}`;

  // Get all top-level comments for this field (excluding replies, as they'll be handled separately)
  const fieldComments = comments
    .filter(
      (comment) =>
        comment.name === threadName && !comment.deleted && !comment.replyId // Only top-level comments
    )
    // Sort by date (oldest first)
    .sort((a, b) => {
      const dateA = a.created instanceof Date ? a.created : new Date(a.created);
      const dateB = b.created instanceof Date ? b.created : new Date(b.created);
      return dateA.getTime() - dateB.getTime();
    });

  const result: Paragraph[] = [];

  const generalComments = fieldComments.filter(
    (c) => c.sectionFrom === null || c.sectionTo === null
  );

  if (!generalComments.length) return [];

  result.push(
    new Paragraph({
      children: [
        new TextRun({
          text: "Common comments of the field",
          italics: true,
          color: "2196F3", // Синий цвет
        }),
      ],
    })
  );

  for (const comment of generalComments) {
    result.push(...renderCommentWithReplies(comment, comments));
  }

  return result;
}

/**
 * Renders comments grouped by text fragments for insertion into text.
 * Для каждого фрагмента выводим заголовок "Comments" и весь тред (коммент + ответы).
 */
function renderFragmentComments(
  fieldId: string | undefined,
  fieldName: string,
  comments: Comment[],
  fieldValue: string,
  sectionFrom: number,
  sectionTo: number
): Paragraph[] {
  if (!fieldId) return [];

  const threadName = `${fieldId}_${fieldName}`;

  // Get comments for this specific fragment
  // Показываем все комментарии, включая черновики (isDraft)
  const fragmentComments = comments
    .filter(
      (comment) =>
        comment.name === threadName &&
        !comment.deleted &&
        !comment.replyId &&
        comment.sectionFrom === sectionFrom &&
        comment.sectionTo === sectionTo
    )
    .sort((a, b) => {
      const dateA = a.created instanceof Date ? a.created : new Date(a.created);
      const dateB = b.created instanceof Date ? b.created : new Date(b.created);
      return dateA.getTime() - dateB.getTime();
    });

  if (!fragmentComments.length) return [];

  const result: Paragraph[] = [];

  // Header "Comments:" - синий, курсив, обычный размер шрифта
  result.push(
    new Paragraph({
      children: [
        new TextRun({
          text: "Comments:",
          italics: true,
          color: "2196F3", // Синий цвет
        }),
      ],
    })
  );

  // Render all comments for this fragment (including replies)
  for (const comment of fragmentComments) {
    result.push(...renderCommentWithReplies(comment, comments));
  }

  return result;
}

/**
 * Рендер Textarea с инлайн-комментариями:
 * - обычный текст рендерится как есть;
 * - текст, к которому привязаны комментарии (sectionFrom/sectionTo),
 *   рендерится курсивом и цветом из поля color (берём цвет первого комментария);
 * - сразу после подсвеченного фрагмента выводится блок "Comments" с комментариями и ответами;
 * - комментарии без sectionFrom/sectionTo выводятся в конце поля, как раньше.
 */
function renderTextareaWithInlineComments(
  heading: string,
  value: string,
  fieldId: string | undefined,
  fieldNameForThread: string,
  comments: Comment[]
): Paragraph[] {
  const result: Paragraph[] = [];

  // Заголовок поля
  result.push(
    new Paragraph({
      text: heading,
      heading: HeadingLevel.HEADING_3,
    })
  );

  if (!value) {
    result.push(
      new Paragraph({
        children: [new TextRun({ text: "-" })],
      })
    );
    result.push(
      ...renderGeneralCommentsForField(fieldId, fieldNameForThread, comments)
    );
    return result;
  }

  const threadName = `${fieldId}_${fieldNameForThread}`;

  // Собираем топовые комментарии к фрагментам
  // Показываем все комментарии, включая черновики (isDraft)
  const fragmentComments = comments.filter(
    (comment) =>
      comment.name === threadName &&
      !comment.deleted &&
      !comment.replyId &&
      comment.sectionFrom !== null &&
      comment.sectionTo !== null
  );

  // Если нет комментариев к фрагментам, рендерим как обычно
  if (fragmentComments.length === 0) {
    result.push(...(renderProseMirrorSchemaToGoogleDoc(value, "-") || []));
    result.push(
      ...renderGeneralCommentsForField(fieldId, fieldNameForThread, comments)
    );
    return result;
  }

  // Группируем по [from,to] и сортируем
  const fragmentGroups = new Map<string, Comment[]>();
  for (const comment of fragmentComments) {
    const key = `${comment.sectionFrom}_${comment.sectionTo}`;
    // Логируем значения из базы данных
    console.log("[immigramForms] Comment from DB:", {
      id: comment.id,
      sectionFrom: comment.sectionFrom,
      sectionTo: comment.sectionTo,
      key,
      text: comment.text?.substring(0, 50),
    });
    if (!fragmentGroups.has(key)) {
      fragmentGroups.set(key, []);
    }
    const group = fragmentGroups.get(key);
    if (group) {
      group.push(comment);
    }
  }

  // Сортируем группы по позиции
  const sortedFragmentKeys = Array.from(fragmentGroups.keys()).sort((a, b) => {
    const fromA = parseInt(a.split("_")[0], 10);
    const fromB = parseInt(b.split("_")[0], 10);
    return fromA - fromB;
  });

  // Сортируем комментарии в каждой группе по дате
  fragmentGroups.forEach((groupComments) => {
    groupComments.sort((a, b) => {
      const dateA = a.created instanceof Date ? a.created : new Date(a.created);
      const dateB = b.created instanceof Date ? b.created : new Date(b.created);
      return dateA.getTime() - dateB.getTime();
    });
  });

  // Создаем массив highlights для функции рендеринга.
  // Позиции sectionFrom и sectionTo в базе трактуем так:
  // - sectionFrom и sectionTo относятся к символам, считаем их 1-based и включительными;
  // - в plain-text индексации (0-based, [from, toExclusive)) это соответствует [sectionFrom-1, sectionTo-1).
  // Формула: [sectionFrom - 1, sectionTo - 1) для эксклюзивного конца
  const highlights: Array<{ from: number; to: number; colorHex?: string }> = [];
  // Маппинг: эксклюзивный to (0-based) -> ключ из базы данных (1-based, формат "fromDb_toDb")
  const fragmentKeyMap = new Map<string, string>();

  for (const key of sortedFragmentKeys) {
    const [fromStr, toStr] = key.split("_");
    const fromDb = parseInt(fromStr, 10);
    const toDb = parseInt(toStr, 10);

    // Переводим в 0-based plain-text индексы по формуле [sectionFrom - 1, sectionTo - 1)
    const from = Math.max(0, fromDb - 1);
    const toExclusive = toDb - 1; // Эксклюзивный конец: sectionTo - 1

    const groupComments = fragmentGroups.get(key);
    if (!groupComments || groupComments.length === 0) {
      continue;
    }
    const colorHex = getCommentColorHex(groupComments[0].color);

    // Логируем созданные highlights (для отладки)
    console.log("[immigramForms] Creating highlight:", {
      key,
      sectionFrom: fromDb,
      sectionTo: toDb,
      fromPlainText: from,
      toExclusive,
      colorHex,
    });

    highlights.push({ from, to: toExclusive, colorHex });
    // Сохраняем маппинг: эксклюзивный to -> ключ из базы данных
    fragmentKeyMap.set(`${from}_${toExclusive}`, key);
  }

  // Создаем callback для вставки комментариев после фрагментов
  const onFragmentEnd = (from: number, to: number): Paragraph[] => {
    // to передается как эксклюзивный конец (0-based)
    const dbKey = fragmentKeyMap.get(`${from}_${to}`);
    if (dbKey && fragmentGroups.has(dbKey)) {
      // Извлекаем оригинальные значения из базы данных (1-based)
      const [fromDbStr, toDbStr] = dbKey.split("_");
      const fromDb = parseInt(fromDbStr, 10);
      const toDb = parseInt(toDbStr, 10);

      return renderFragmentComments(
        fieldId,
        fieldNameForThread,
        comments,
        value,
        fromDb, // Передаем оригинальные значения из базы (1-based)
        toDb
      );
    }
    return [];
  };

  // Рендерим весь текст с выделениями и вставкой комментариев
  // Функция renderProseMirrorSchemaToGoogleDocWithHighlights теперь
  // автоматически вставляет комментарии сразу после каждого фрагмента
  const textParagraphs =
    renderProseMirrorSchemaToGoogleDocWithHighlights(
      value,
      highlights,
      "-",
      onFragmentEnd
    ) || [];

  result.push(...textParagraphs);

  // Комментарии, не привязанные к фрагментам, в конце
  result.push(
    ...renderGeneralCommentsForField(fieldId, fieldNameForThread, comments)
  );

  return result;
}

export function renderImmigramFormToGoogleDoc(
  t: TFunction,
  forms: FormWithDataAndConfig[],
  comments?: Comment[]
) {
  return forms.map(({ fields, values }) => {
    const filteredFields = fields?.filter((field) => values[field.name]);

    return (
      filteredFields?.map((field) => {
        const fieldData = values[field.name];
        const fieldId = fieldData?.id;
        const fieldNameForThread = fieldData?.name || field.name;
        if (field.type === ImmigramFormElementType.Checkbox) {
          const optionsMap = keyBy(field.options, "value");
          const selectedOptions = values[field.name].value
            ?.split(",")
            .map((optionValue) => optionsMap[optionValue]);

          const fieldComments = comments
            ? renderGeneralCommentsForField(
                fieldId,
                fieldNameForThread,
                comments
              )
            : [];

          return [
            new Paragraph({
              text: field.title
                ? getModifiedConfigParamValue(t, field.title, {
                    forceText: true,
                  })
                : field.name,
              heading: HeadingLevel.HEADING_3,
            }),
            selectedOptions?.map((option) => [
              new Paragraph({
                bullet: {
                  level: 0,
                },
                children: [
                  new TextRun({
                    text: getModifiedConfigParamValue(t, option?.title, {
                      forceText: true,
                    }) as string,
                  }),
                ],
              }),
            ]) || null,
            ...fieldComments,
          ];
        }
        if (field.type === ImmigramFormElementType.Date) {
          const value = values[field.name].value;
          const fieldComments = comments
            ? renderGeneralCommentsForField(
                fieldId,
                fieldNameForThread,
                comments
              )
            : [];

          return [
            new Paragraph({
              text: field.title
                ? getModifiedConfigParamValue(t, field.title, {
                    forceText: true,
                  })
                : field.name,
              heading: HeadingLevel.HEADING_3,
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: value || "-",
                }),
              ],
            }),
            ...fieldComments,
          ];
        }
        if (field.type === ImmigramFormElementType.Select) {
          const optionsMap = keyBy(field.options, "value");
          const selectedOptions = values[field.name].value
            ?.split(",")
            .map((optionValue) => optionsMap[optionValue]);

          const fieldComments = comments
            ? renderGeneralCommentsForField(
                fieldId,
                fieldNameForThread,
                comments
              )
            : [];

          return [
            new Paragraph({
              text: field.title
                ? getModifiedConfigParamValue(t, field.title, {
                    forceText: true,
                  })
                : field.name,
              heading: HeadingLevel.HEADING_3,
            }),
            selectedOptions?.map((option) => [
              new Paragraph({
                bullet: {
                  level: 0,
                },
                children: [
                  new TextRun({
                    text: getModifiedConfigParamValue(t, option?.title, {
                      forceText: true,
                    }) as string,
                  }),
                ],
              }),
            ]) || null,
            ...fieldComments,
          ];
        }
        if (field.type === ImmigramFormElementType.Text) {
          const value = values[field.name].value;
          const fieldComments = comments
            ? renderGeneralCommentsForField(
                fieldId,
                fieldNameForThread,
                comments
              )
            : [];

          if (field?.tags?.includes("doc-formatter-project-name")) {
            return [
              new Paragraph({
                children: [
                  new TextRun({
                    text: field.title
                      ? `${getModifiedConfigParamValue(t, field.title, {
                          forceText: true,
                        })}: `
                      : `${field.name}: `,
                    bold: true,
                    size: 36,
                  }),
                  new TextRun({
                    text: value || "-",
                    bold: true,
                    size: 36,
                  }),
                ],
                heading: HeadingLevel.HEADING_3,
              }),
              ...fieldComments,
            ];
          } else {
            return [
              new Paragraph({
                text: field.title
                  ? getModifiedConfigParamValue(t, field.title, {
                      forceText: true,
                    })
                  : field.name,
                heading: HeadingLevel.HEADING_3,
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: value || "-",
                  }),
                ],
              }),
              ...fieldComments,
            ];
          }
        }
        if (field.type === ImmigramFormElementType.Textarea) {
          const value = values[field.name].value || "";

          if (!comments || comments.length === 0) {
            // Нет комментариев – рендерим как раньше
            return [
              new Paragraph({
                text: field.heading
                  ? getModifiedConfigParamValue(t, field.heading, {
                      forceText: true,
                    })
                  : field.name,
                heading: HeadingLevel.HEADING_3,
              }),
              renderProseMirrorSchemaToGoogleDoc(value, "-"),
            ];
          }

          const heading =
            (field.heading &&
              (getModifiedConfigParamValue(t, field.heading, {
                forceText: true,
              }) as string)) ||
            field.name;

          return renderTextareaWithInlineComments(
            heading,
            value,
            fieldId,
            fieldNameForThread,
            comments
          );
        }
        if (field.type === ImmigramFormElementType.Toggle) {
          const value = values[field.name].value;
          const fieldComments = comments
            ? renderGeneralCommentsForField(
                fieldId,
                fieldNameForThread,
                comments
              )
            : [];

          return [
            new Paragraph({
              text: field.title
                ? getModifiedConfigParamValue(t, field.title, {
                    forceText: true,
                  })
                : field.name,
              heading: HeadingLevel.HEADING_3,
            }),
            new Paragraph({
              text: value || "-",
            }),
            ...fieldComments,
          ];
        }
        return null;
      }) || null
    );
  });
}
